import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type SubmitEvent,
} from 'react';

import debounce from 'lodash/debounce';
import { usePathname, useSearchParams } from 'next/navigation';
import { type FocusableElement, tabbable } from 'tabbable';

import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { getRouteView } from '@/modules/activityTracker/referers';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type SupportedLocation } from '@/modules/location/constants';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { ROUTE_CHANGE_START_EVENT } from '@/modules/routing/constants';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { SearchUrlController } from '@/modules/search/SearchUrlController';
import { type CategorySearchSuggestion } from '@/modules/search/aliases';
import { getCategorySearchSuggestions } from '@/modules/search/api';
import { getSearchHistory, setSearchHistory } from '@/modules/search/helpers';
import { findNextTabbableElementOutsideSuggestions } from '@/modules/search/helpers/findNextTabbableElementOutsideSuggestions';
import { navigateNonTabbableElements } from '@/modules/search/helpers/navigateNonTabbableElements';
import { useTrendingSearches } from '@/modules/search/hooks/useTrendingSearches';
import {
  type SearchSuggestionOption,
  type SearchSuggestionType,
} from '@/modules/search/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

function getChildLink(element: FocusableElement) {
  return element.querySelector('a') as HTMLElement;
}

function getLabelValue(val: string) {
  return {
    label: val,
    value: val.replace(/\*\*/g, ''),
  };
}

export const DEBOUNCE_SEARCH_DELAY = 200;
export const SUGGESTION_TYPE_QUERY_KEY = '_suggestion-type';

export function useSearchBarUtilities() {
  const instanceId = useId();
  const router = useExtendedRouter();
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const { location } = useCurrentLocation();
  const trendingSearchSuggestions = useTrendingSearches(
    location as SupportedLocation
  );
  const queryParams = useSearchParams();
  const urlQuery = queryParams.get('q');
  const path = usePathname();

  const [isFocused, setIsFocused] = useState(false);
  const [isUserInput, setIsUserInput] = useState(true);
  const [originalUserSearch, setOriginalUserSearch] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<
    SearchSuggestionOption[]
  >([]);

  const [isRefined, setIsRefined] = useState(false);
  const lastActiveElement = useRef<{ element: FocusableElement | null }>({
    element: null,
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const urlController = new SearchUrlController();
  const recentSuggestionsRef = useRef<HTMLUListElement>(null);
  const clearSuggestionsRef = useRef<HTMLButtonElement>(null);
  const searchSuggestionsRef = useRef<HTMLUListElement>(null);
  const trendingSuggestionsRef = useRef<HTMLUListElement>(null);
  const userSuggestionsRef = useRef<HTMLUListElement>(null);
  // we were getting memory leak warnings from state updates
  // after component was un-mounted. This eliminated them
  // from tests and hopefully will improve performance a bit
  const isMounted = useRef(true);

  const [searchBarValue, setSearchBarValue] = useState('');
  const [recentSearchItems, setRecentSearchItems] = useState<
    SearchSuggestionOption[]
  >(getSearchHistory() || []);

  function resetSearchSuggestions() {
    setSearchSuggestions([]);
  }

  function formatSuggestionsForSelect(suggestions: CategorySearchSuggestion[]) {
    if (isMounted.current) {
      const formattedSuggestions = suggestions.map((suggestion) => {
        return {
          ...suggestion,
          ...getLabelValue(suggestion.value),
        };
      });
      setSearchSuggestions(formattedSuggestions);
    }
  }

  useEffect(() => {
    if (searchSuggestions.length && !searchBarValue) {
      resetSearchSuggestions();
    }
  }, [searchBarValue, searchSuggestions]);

  const fetchCategorySearchSuggestions = useCallback(
    (newValue: string) => getCategorySearchSuggestions(newValue, location),
    [location]
  );
  const fetchSelectOptions = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce((value) => {
      fetchCategorySearchSuggestions(value)
        .then((resp) => {
          formatSuggestionsForSelect(resp.data);
        })
        .catch(() => {
          resetSearchSuggestions();
        });
    }, DEBOUNCE_SEARCH_DELAY),
    [location, fetchCategorySearchSuggestions]
  );

  function handleResetSuggestions() {
    setIsRefined(false);
    resetSearchSuggestions();
  }

  function handleSearchPageLoad(url: string) {
    const isSearchResultsView = getRouteView(url) === 'searchResults.view';
    if (isSearchResultsView) {
      const input = rootRef.current?.querySelector('input');
      input?.blur();
    }
  }

  useEffect(() => {
    handleSearchPageLoad(path);
  }, [path]);

  useEffect(() => {
    const searchQuery = Array.isArray(urlQuery)
      ? urlQuery.join(' ')
      : urlQuery || '';
    if (searchBarValue !== searchQuery) {
      setSearchBarValue(searchQuery);
    }
  }, [urlQuery]);

  function sendClearSearchBarActionEvent() {
    return sendActivityTrackerEvent(
      ActivityTrackerEventType.CLEAR_SEARCH_BAR_ACTION
    );
  }

  function handleClearButtonClick() {
    sendClearSearchBarActionEvent();
    setSearchBarValue('');
    setIsUserInput(true);
    const input = rootRef.current?.querySelector('input');
    input?.focus();
  }

  function setInput(event: ChangeEvent<HTMLInputElement>) {
    const {
      target: { value },
    } = event;
    if (value === '') {
      sendClearSearchBarActionEvent();
      setIsRefined(false);
    }
    setIsUserInput(true);
    setIsRefined(true);
    setSearchBarValue(value);
  }

  function updateFocus() {
    setIsFocused((isFocused: boolean) => !isFocused);
  }

  function onBlur() {
    updateFocus();
  }

  function onFocus(sendATEvent = true) {
    setMenuIsOpen(true);
    updateFocus();

    if (sendATEvent) {
      sendActivityTrackerEvent(ActivityTrackerEventType.SEARCH_BAR_VIEW);
    }
  }

  function detectUserMention(searchTerm: string) {
    const userMention = new RegExp('^@([a-zA-Z0-9_]+)');
    return userMention.test(searchTerm);
  }

  /* Recent Search Logic */
  function clearRecentSearchHistory() {
    if (lastActiveElement.current) {
      lastActiveElement.current.element = null;
    }
    setRecentSearchItems([]);
    setSearchHistory([]);
  }

  function formatSearchValue(
    value: string | SearchSuggestionOption,
    path = ''
  ) {
    if (typeof value === 'string') {
      return { ...getLabelValue(value), path };
    }
    return {
      ...value,
      ...getLabelValue(value.value),
      path,
    };
  }

  function updateRecentSearchHistory(suggestionValue?: string, path = '') {
    const searchHist = getSearchHistory() || [];
    const searchHistory = [...searchHist];
    const formattedRecentSearchHistory = formatSearchValue(
      suggestionValue || searchBarValue,
      path
    );
    searchHistory.unshift(formattedRecentSearchHistory);
    const uniqueRecentSearches = searchHistory.filter(
      (suggestion: SearchSuggestionOption, index: number, self) =>
        index ===
        self.findIndex(
          (comparedSuggestion) => comparedSuggestion.label === suggestion.label
        )
    );
    if (uniqueRecentSearches.length > 5) {
      uniqueRecentSearches.pop();
    }
    setRecentSearchItems(uniqueRecentSearches);
    setSearchHistory(uniqueRecentSearches);
  }

  function updateRoute(
    searchSelection: Pick<
      CategorySearchSuggestion,
      'value' | 'group' | 'gender' | 'is_kids'
    > & {
      suggestionType?: SearchSuggestionType;
    }
  ) {
    handleResetSuggestions();
    setRecentSearchItems(getSearchHistory());
    const { value, suggestionType, group, gender, is_kids } = searchSelection;
    if (value) {
      const filterValues = urlController.fromUrl(
        Object.fromEntries(queryParams.entries())
      );
      const { searchParams } = urlController.toUrl(filterValues, value, true);
      if (suggestionType) {
        searchParams.set(SUGGESTION_TYPE_QUERY_KEY, suggestionType);
      } else {
        searchParams.delete(SUGGESTION_TYPE_QUERY_KEY);
      }

      const newSearchParams = new URLSearchParams(searchParams);
      updateRecentSearchHistory(value, newSearchParams.toString());
      setMenuIsOpen(false);

      const query = {
        q: value,
        ...(group ? { groups: group } : {}),
        ...(gender ? { gender } : {}),
        ...(is_kids ? { isKids: is_kids.toString() } : {}),
        ...(suggestionType && { [SUGGESTION_TYPE_QUERY_KEY]: suggestionType }),
      };
      const qs = new URLSearchParams(query).toString();
      const url = `/search/?${qs}`;

      return router.push(url);
    }
    return router.push('/search/');
  }

  function handleSubmit(event?: SubmitEvent<HTMLFormElement>) {
    event?.preventDefault();
    event?.stopPropagation();
    setMenuIsOpen(false);
    setIsFocused(false);
    /*
          To avoid duplicate searches on searches that had sanitized slugs,
          e.g. emojis
          compare current searchInput against the unsanitized slug
          instead of comparing it against the searched value which has already been sanitized
          */
    if (!searchBarValue || urlQuery === searchBarValue.trim()) {
      return false;
    }
    if (detectUserMention(searchBarValue)) {
      return router.push(`/${searchBarValue.slice(1)}/`);
    }
    return updateRoute({ value: searchBarValue });
  }

  useEffect(() => {
    // Due to react 18 strict mode behaviour we need to reset isMounted to true
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (searchSuggestions.length && !searchBarValue) {
      resetSearchSuggestions();
    }
  }, [searchBarValue, searchSuggestions]);

  useEffect(() => {
    if ((!isUserInput || urlQuery === searchBarValue) && isRefined) {
      return;
    }
    if (!searchBarValue.length || !isRefined) {
      resetSearchSuggestions();
      setIsRefined(false);
      return;
    }
    fetchSelectOptions(searchBarValue);
    return;
  }, [searchBarValue, fetchSelectOptions]);

  function setSuggestionAsSearch(
    suggestion: SearchSuggestionOption,
    suggestionType: SearchSuggestionType
  ) {
    const { value, group, gender, is_kids } = suggestion;
    setSearchBarValue(value);
    setIsRefined(false);
    updateRoute({ value, group, gender, is_kids, suggestionType });
  }

  function handleOutsideClick() {
    setMenuIsOpen(false);
  }

  useEffect(() => {
    function closeMenu() {
      setMenuIsOpen(false);
    }

    document.addEventListener(ROUTE_CHANGE_START_EVENT, closeMenu);

    return () => {
      document.removeEventListener(ROUTE_CHANGE_START_EVENT, closeMenu);
    };
  }, []);

  useEffect(() => {
    if (isUserInput) {
      setOriginalUserSearch(searchBarValue);
    }

    function handleKeydown(event: KeyboardEvent) {
      if (
        !rootRef.current ||
        (!searchSuggestionsRef.current &&
          !trendingSuggestionsRef.current &&
          !userSuggestionsRef.current)
      ) {
        return;
      }
      //lock down the last user input
      setIsUserInput(false);
      const tabbableElms = tabbable(rootRef.current);
      // Get suggestion elements from children since they have tabIndex={-1} and won't be found by tabbable()
      const { clearSuggestionButton, suggestionElms } =
        navigateNonTabbableElements({
          tabbableElms,
          recentSuggestionsRef,
          clearSuggestionsRef,
          searchSuggestionsRef,
          trendingSuggestionsRef,
          userSuggestionsRef,
        });
      if (!suggestionElms.length) {
        return;
      }
      const activeElm = document.activeElement as FocusableElement;
      const activeElmIsFirstSuggestion =
        suggestionElms.indexOf(activeElm) === 0;

      if (event.key === 'ArrowUp') {
        event.preventDefault();

        //if the user is in the first item of the suggestion list we only want to key up to the input field
        if (activeElmIsFirstSuggestion || !activeElm) {
          setSearchBarValue(originalUserSearch);
          tabbableElms[0]?.focus();
          return;
        }
        // If active element is in suggestions, navigate to previous suggestion
        const activeSuggestionIndex = suggestionElms.indexOf(activeElm);
        if (activeSuggestionIndex > 0) {
          const prev = suggestionElms[activeSuggestionIndex - 1];
          if (prev) {
            prev.focus();
          }
          return;
        }
        // If active element is not in suggestions and not the input, go to input
        if (tabbableElms.indexOf(activeElm) === -1) {
          setSearchBarValue(originalUserSearch);
          tabbableElms[0]?.focus();
          return;
        }
        return;
      }

      if (event.key === 'Escape') {
        setMenuIsOpen(false);
        tabbableElms[0].blur();
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const activeElmIsSearchInput = tabbableElms.indexOf(activeElm) === 0;
        const activeSuggestionIndex = suggestionElms.indexOf(activeElm);
        const activeElmIsLastSuggestion =
          activeSuggestionIndex === suggestionElms.length - 1;

        //if the user is in the input field we only want to key down into suggestions
        if (suggestionElms.length && activeElmIsSearchInput) {
          suggestionElms[0].focus();
          return;
        }
        //if the user is on the last item of the suggestions send them back to the input field
        if (suggestionElms.length && activeElmIsLastSuggestion) {
          setSearchBarValue(originalUserSearch);
          tabbableElms[0]?.focus();
          return;
        }
        // If active element is in suggestions, navigate to next suggestion
        if (
          activeSuggestionIndex >= 0 &&
          activeSuggestionIndex < suggestionElms.length - 1
        ) {
          const next = suggestionElms[activeSuggestionIndex + 1];
          if (next) {
            next.focus();
          }
          return;
        }
        // If active element is not in suggestions and not the input, go to input
        if (!activeElm || tabbableElms.indexOf(activeElm) === -1) {
          tabbableElms[0]?.focus();
          return;
        }
      }

      if (!event.shiftKey && event.key === 'Tab') {
        /*
        - Preventing tabbing through the suggestions list.
        - Suggestions can be accessed with the arrow buttons.
        - Tabbing will only navigate further down the website.
        - Close menu when tabbing away from search bar container.
        */
        const isNavigatingWithinSuggestions =
          suggestionElms.includes(activeElm);
        const navEl = document.getElementById('mainNavigation');
        const clearButton = document.getElementById(
          `searchBar__clear-btn${instanceId}`
        );

        const isWithinSearchBar = rootRef.current?.contains(activeElm);

        /*
        Close menu when tabbing away from search bar container.
        Keep menu open when tabbing within search bar (input, clear button, suggestions).
        */
        if (!isWithinSearchBar) {
          setMenuIsOpen(false);
        }

        // Only intercept tab when starting from input to redirect to clear button
        // Allow normal tabbing through other search bar elements (clear button, submit button)
        const isOnInput = tabbableElms.indexOf(activeElm) === 0;
        const submitButton = document.getElementById(
          `searchBar__submit-btn${instanceId}`
        );
        const isOnSubmitButton = activeElm === submitButton;

        // Focuses on the clear button if starting from input with a value
        if (
          searchBarValue &&
          isOnInput &&
          !isNavigatingWithinSuggestions &&
          isWithinSearchBar &&
          clearButton
        ) {
          event.preventDefault();
          clearButton.focus();
        } else if (
          isOnSubmitButton &&
          !isNavigatingWithinSuggestions &&
          isWithinSearchBar
        ) {
          // When tabbing from submit button, skip all suggestion elements
          // and go to the next tabbable element outside suggestions
          event.preventDefault();
          const nextElement = findNextTabbableElementOutsideSuggestions({
            activeElm,
            recentSuggestionsRef,
            searchSuggestionsRef,
            trendingSuggestionsRef,
            userSuggestionsRef,
          });

          if (nextElement) {
            nextElement.focus();
            setMenuIsOpen(false);
          } else if (navEl) {
            const links: FocusableElement[] | null = tabbable(navEl);
            links[0]?.focus();
            setMenuIsOpen(false);
          }
        } else if (
          navEl &&
          !isNavigatingWithinSuggestions &&
          !isWithinSearchBar
        ) {
          event.preventDefault();
          const links: FocusableElement[] | null = tabbable(navEl);
          links[0]?.focus();
        }
      }

      if (event.shiftKey && event.key === 'Tab') {
        /*
       Prevent tabbing back through the suggestions list.
       Close menu when tabbing away from search bar container.
       */
        const isNavigatingWithinSuggestions =
          suggestionElms.includes(activeElm);
        const isWithinSearchBar = rootRef.current?.contains(activeElm);

        // Close menu when tabbing away from search bar container
        // Keep menu open when tabbing within search bar (input, clear button, suggestions)
        if (!isWithinSearchBar) {
          setMenuIsOpen(false);
        }

        if (!isNavigatingWithinSuggestions && !isWithinSearchBar) {
          event.preventDefault();
          const depopLogo = document.querySelector(
            'a[href="/"]'
          ) as HTMLElement;
          depopLogo?.focus();
        }
      }
      // To fire when pressing enter on a suggestion
      if (searchBarValue && event.key === 'Enter') {
        if (document.activeElement === clearSuggestionButton) {
          clearRecentSearchHistory();
        } else {
          getChildLink(activeElm)?.click();
        }
      }
    }

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [rootRef, searchBarValue]);

  return {
    instanceId,
    inputValue: searchBarValue,
    isFetchingNewSearch: false, // TODO
    isFocused,
    menuIsOpen,
    searchSuggestions,
    recentSearchItems,
    rootRef,
    recentSuggestionsRef,
    clearSuggestionsRef,
    fetchCategorySearchSuggestions,
    formatSuggestionsForSelect,
    handleSubmit,
    onBlur,
    onFocus,
    setInput,
    isUserInput,
    setIsUserInput,
    originalUserSearch,
    setOriginalUserSearch,
    setValue: setSearchBarValue,
    setIsRefined,
    setSuggestionAsSearch,
    clearRecentSearchHistory,
    updateFocus,
    handleClearButtonClick,
    isRefined,
    searchSuggestionsRef,
    handleOutsideClick,
    trendingSuggestionsRef,
    userSuggestionsRef,
    trendingSearchSuggestions,
  };
}
